/* eslint-disable react/prop-types */
import React, { FC, useState, MouseEvent } from 'react';
import { Box, Button, Grid, IconButton, MenuItem, Popover, Select, ClickAwayListener, Typography, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import moment from 'moment';
import { saveAs } from 'file-saver';
import useStyles from './styles';
import FileUploader from '../../../file-loader';
import { ActionTypes, ConfigType, DeviceParameterConfigType } from '../../../../pages/bulk-device-management/types';
import { csvExportCommand, handleTepmlateDownloadFireAndForget } from '../../csv-template';
import { uploadFireAndForgetCSV } from '../../../../store/bulk-device-flash/actions';
import InfoIcon from '../../../../assets/icons/info-icon';
import ConfigInformation from '../config-information';
import DownloadIcon from '../../../../assets/icons/download-icon';
import theme from '../../../../globals/theme';
import { Command } from '../../../../entities/commands-v1/command';
import { AppState } from '../../../../store';
import ShortCommandV2 from '../../../../services/short-command-v2';
import { enqueueSnackbarAction } from '../../../../store/snackbar/actions';

interface BulkConfigProperties {
  onActionTypeChange: (actionType: string) => void;
  setScheduleCommandResponse: (response: any) => void;
  scheduleCommandResponse?: any;
}

function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
// eslint-disable-next-line react/prop-types
const BulkConfig: FC<BulkConfigProperties> = ({
    onActionTypeChange = () => {},
    setScheduleCommandResponse = () => {},
    scheduleCommandResponse,
}) => {
    const classes = useStyles();
    const [actionType, setActionType] = useState<string>('Action Type');
    const [subActionType, setSubActionType] = useState<string>('Parameter Type');
    const commands: Command[] = useSelector((state: AppState) => state.commands.commands);
    const dispatch = useDispatch();

    const actionTypeOptions: ActionTypes[] = [
        { title: 'SMH Adjust', value: ConfigType.SMHAdjust, disabled: true },
        { title: 'Short Message', value: ConfigType.ShortMessage, disabled: false },
        { title: 'Short Command', value: ConfigType.ShortCommand, disabled: false },
        { title: 'Config Files', value: ConfigType.ConfigFiles, disabled: false },
        { title: 'Set Device Parameters', value: ConfigType.SetDeviceParameters, disabled: false },
        { title: 'Subscription & PL Scheduling', value: ConfigType.Subscription, disabled: true },
        { title: 'Device Wakeups', value: ConfigType.DeviceWakeups, disabled: false },
        // The 'Schedule Command' option is intentionally disabled pending further backend/API support and QA. The implementation is present for future enablement.
        { title: 'Schedule Command', value: ConfigType.ScheduleCommand, disabled: true },
    ];

    const deviceParameterOptions: ActionTypes[] = [
        { title: 'EOD', value: DeviceParameterConfigType.EOD, disabled: false },
        { title: 'Tamper Resistance', value: DeviceParameterConfigType.TamperResistance, disabled: true },
    ];

    const [iconButtonReference, setIconButtonReference] = useState<HTMLElement | null>(null);
    const open = Boolean(iconButtonReference);

    const handleClick = (event: MouseEvent<HTMLButtonElement> | null, configType: ConfigType, subActionType: string) => {
        setIconButtonReference(event && event.currentTarget && event.currentTarget.parentElement);
    };

    const clickAway = () => {
        setIconButtonReference(null);
    };
    const { client, envVariables } = useSelector((state: AppState) => state.login);

    const handleScheduleCommandSelected = async () => {
        const ScheduleCommand = new ShortCommandV2(client, envVariables.shortCommandsV2BaseUrl);
        try {
            const response = await ScheduleCommand.getScheduleCommands();
            if (response && response.data?.configs) {
                setScheduleCommandResponse(response.data.configs); 
            } else {
                setScheduleCommandResponse([]); 
            }
        } catch (error) {
            setScheduleCommandResponse([]); 
        }
    };

    const exportCsv = async () => {
        let exportData: Array<any> = [];
        let csvHeader: Array<string> = [];

        if (actionType === ConfigType.ScheduleCommand && Array.isArray(scheduleCommandResponse) ) {
            exportData = scheduleCommandResponse;
            csvHeader = [
                "deviceId",
                "data",
                "fileType",
                "frequency",
                "updatedBy"
            ];
        } else {
            exportData = commands;
            csvHeader = csvExportCommand.split(',');
        }

        const csvRows: Array<string> = [];
        exportData.forEach((item: any) => {
            const row: Array<any> = [];
            csvHeader.forEach((key: any) => {
                row.push(item[key] !== undefined ? item[key] : '');
            });
            csvRows.push(`${row.join(',')}`);
        });

        const blob = new Blob(
            [`${csvHeader.join(',')}\n${csvRows.join('\n')}`],
            { type: 'text/plain;charset=utf-8' }
        );
        await saveAs(blob, `export_command_${moment().format('MMMM_Do_YYYY_h:mm:ss_a')}.csv`);
        await delay(500);
        dispatch(
            enqueueSnackbarAction({
                message: 'Exported Successfully',
                options: { key: (Date.now() + Math.random()).toString(), variant: 'success' },
            }),
        );
    };
    const offset = iconButtonReference ? iconButtonReference.offsetLeft : 0;
   
    return (
        <Box p={4} pb={3} pt={1}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={3} lg={2}>
                    <Select
                        variant="standard"
                        id="actionType"
                        label="Action Type"
                        value={actionType}
                        onChange={(event) => {
                            const selected = event.target.value as ConfigType;
                            setActionType(event.target.value as ConfigType);
                            setSubActionType('Parameter Type');
                            onActionTypeChange(event.target.value as ConfigType);
                            if (selected === ConfigType.ScheduleCommand) {
                                handleScheduleCommandSelected();
                            }
                        }
                        }
                        className={classes.select}
                    >
                        <MenuItem value="Action Type">
                            <em className={classes.unSelected}>Action Type</em>
                        </MenuItem>
                        {(actionTypeOptions as Array<ActionTypes>).map((o: ActionTypes) => (
                            <MenuItem value={o.value} disabled={o.disabled}>{o.title}</MenuItem>
                        ))}
                    </Select>
                </Grid>

                {actionType === ConfigType.SetDeviceParameters &&
                    <Grid item xs={12} md={3} lg={2}>
                        <Select
                            variant="standard"
                            id="subActionType"
                            label="Select Parameter"
                            value={subActionType}
                            onChange={(event) =>
                                setSubActionType(event.target.value as DeviceParameterConfigType)
                            }
                            className={classes.select}
                        >
                            <MenuItem value="Parameter Type">
                                <em className={classes.unSelected}>Parameter Type</em>
                            </MenuItem>
                            {(deviceParameterOptions as Array<ActionTypes>).map((o: ActionTypes) => (
                                <MenuItem value={o.value} disabled={o.disabled}>{o.title}</MenuItem>
                            ))}
                        </Select>
                    </Grid>
                }
                <Grid item xs={6} md={6} lg={8}>
                    <Box display="flex" alignItems="center">
                        {((actionType === ConfigType.SetDeviceParameters && (subActionType === DeviceParameterConfigType.EOD
                                || subActionType === DeviceParameterConfigType.TamperResistance)) || actionType === ConfigType.ShortMessage || actionType === ConfigType.ShortCommand
                                || actionType === ConfigType.ConfigFiles ||
                                actionType === ConfigType.DeviceWakeups || actionType === ConfigType.ScheduleCommand) && (
                                    <IconButton
                                        id="infoButton"
                                        color="primary"
                                        onClick={(event) => handleClick(event, actionType, subActionType)}
                                        size="large"
                                        className={classes.iconMargin}
                                    >
                                        <InfoIcon />
                                    </IconButton >
                                )}
                            {open && (<Popover
                                open={open}
                                anchorEl={iconButtonReference}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                            >
                                <ClickAwayListener onClickAway={clickAway}>
                                    <div style={{ width: 550, marginBottom: "5px" }}>
                                        <ConfigInformation
                                            actionType={actionType}
                                            subActionType={subActionType}
                                        />
                                    </div>
                                </ClickAwayListener>
                            </Popover>
                            )}

                            <Button
                                className={clsx(classes.button, classes.buttonMargin)}
                                variant="outlined"
                                size="small"
                                disabled={(actionType === 'Action Type') || (actionType === 'setDeviceParameters' && subActionType === 'Parameter Type')}
                                onClick={() => handleTepmlateDownloadFireAndForget(actionType, subActionType)}
                            >
                                Download Template
                            </Button>
                            <FileUploader
                                text="Upload CSV"
                                handleFile={(file: any) => dispatch(uploadFireAndForgetCSV(file, actionType, subActionType))}
                                enabled={(actionType !== 'Action Type') ? (actionType === 'setDeviceParameters' ? subActionType !== 'Parameter Type' : true) : subActionType !== 'Parameter Type'}
                            />
                        {actionType === ConfigType.ScheduleCommand && Array.isArray(scheduleCommandResponse) && scheduleCommandResponse.length > 0 && (
                            <Box display="flex" alignItems="center" ml={2}>
                                <Typography variant='h6' style={{ marginRight: 8 }}>Export Commands</Typography>
                                <Tooltip title="Export Command" enterDelay={600}>
                                    <IconButton
                                        onClick={exportCsv}
                                        id="exportButton"
                                        size="large"
                                    >
                                        <DownloadIcon fill={theme.palette.grey[600]} />
                                    </IconButton>
                                </Tooltip>
                        </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BulkConfig;